import { useMutation } from '@apollo/client/react'
import {
  Button,
  ConfirmationDialog,
  FailureNotice,
  Notice,
  PricingSection,
  Section,
  Textarea,
  useTranslate,
} from '@athena/ui'
import { PLAN_CODES, PLANS, comparePlans, isPaidPlan } from '@athena/shared'
import { useRef, useState } from 'react'

import {
  CancelScheduledPlanChangeDocument,
  RequestPlanUpgradeDocument,
  SchedulePlanChangeDocument,
  WithdrawUpgradeRequestDocument,
  type SubscriptionFieldsFragment,
  type PlanFieldsFragment,
} from '../../graphql/generated/operations.js'
import { useFormat } from '../../lib/format.js'
import { useFirstUserErrorText } from '../../lib/user-errors.js'

import { planName } from './planName.js'

/**
 * One plan change, resolved to what the server said: its refusals — empty for
 * a change it made — or `null` when no answer came back with the change in it.
 */
type PlanCall = () => Promise<
  readonly { code: string; message: string; params: readonly string[] }[] | null
>

export interface PlanActionsProps {
  subscription: SubscriptionFieldsFragment
  plans: readonly PlanFieldsFragment[]
}

/**
 * Asking for more, and giving some back.
 *
 * ── There is no checkout here, and that is the design ────────────────────
 *
 * V1 takes no payment. A tutor cannot put themselves on a paid plan, so the
 * upgrade control **sends a request** and says so: the copy names what happens
 * next — somebody sets the plan up and gets in touch — and the confirmation
 * afterwards says the request was sent, never that anything was subscribed or
 * paid. Rendering a card form here would be the one thing this screen must not
 * do.
 *
 * Downgrades are the tutor's own and take effect at the end of the period that
 * is paid for, so the button says which date. No refund is offered because
 * nothing here could issue one.
 *
 * ── Which controls appear ────────────────────────────────────────────────
 *
 * `subscription.actions` decides, and it comes from the server. A screen that
 * worked out for itself whether an upgrade request was allowed would be a
 * second implementation of the rule, and the one that mattered would still be
 * on the server — so this file reads the list rather than reconstructing it.
 */
export function PlanActions({ subscription, plans }: PlanActionsProps) {
  const t = useTranslate()
  const format = useFormat()
  const userErrorText = useFirstUserErrorText()

  const [selectedPlan, setSelectedPlan] = useState<PlanFieldsFragment['code'] | null>(null)
  const [message, setMessage] = useState('')
  const [target, setTarget] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState<string | null>(null)
  /** A change that never got an answer, and the call to send again. */
  const [failure, setFailure] = useState<unknown>()
  const lastCall = useRef<PlanCall | null>(null)

  const refetch = { refetchQueries: ['MySubscription'], awaitRefetchQueries: true }
  const [requestUpgrade, requesting] = useMutation(RequestPlanUpgradeDocument, refetch)
  const [withdraw, withdrawing] = useMutation(WithdrawUpgradeRequestDocument, refetch)
  const [scheduleChange, scheduling] = useMutation(SchedulePlanChangeDocument, refetch)
  const [cancelChange, cancelling] = useMutation(CancelScheduledPlanChangeDocument, refetch)

  const can = (action: string) => subscription.actions.includes(action as never)

  /** The plans above the current one: what an upgrade request can ask for. */
  const larger = PLAN_CODES.filter((code) => comparePlans(subscription.planCode, code) < 0)
  /** The plans below it: what a downgrade can move to, Free included. */
  const smaller = PLAN_CODES.filter((code) => comparePlans(subscription.planCode, code) > 0)

  return (
    <div className="flex flex-col gap-6">
      {/* One place for a refusal, whichever control caused it.
          It was briefly attached to the message field instead, which was
          wrong twice over: a refused *plan change* is not a problem with what
          the tutor typed, and a screen whose only error surface was a field
          could swallow a refusal from a control that has no field at all. */}
      {error ? <Notice tone="error" title={error} /> : null}
      {failure === undefined ? null : (
        <FailureNotice
          title={t('subscription.changeNotMade')}
          error={failure}
          retrying={
            requesting.loading || withdrawing.loading || scheduling.loading || cancelling.loading
          }
          onRetry={() => {
            if (lastCall.current) void run(lastCall.current)
          }}
        />
      )}

      {subscription.pendingRequest ? (
        <Notice
          title={t('subscription.pendingHeading')}
          actions={
            can('WITHDRAW_UPGRADE_REQUEST') ? (
              <Button
                variant="secondary"
                size="sm"
                loading={withdrawing.loading}
                onClick={() => {
                  void run(() =>
                    withdraw({
                      variables: { requestId: subscription.pendingRequest?.id ?? '' },
                    }).then((result) => result.data?.withdrawUpgradeRequest.userErrors ?? null),
                  )
                }}
              >
                {t('subscription.pendingWithdraw')}
              </Button>
            ) : null
          }
        >
          {/* Says plainly that nothing has changed. A tutor who has asked for
              Premium and reads "pending" must not come away thinking they are
              on it. */}
          {t('subscription.pendingBody', [
            planName(t, subscription.pendingRequest.requestedPlanCode),
            format.date(subscription.pendingRequest.createdAt),
            planName(t, subscription.planCode),
          ])}
        </Notice>
      ) : null}

      {declined() ? (
        <Notice tone="warning" title={t('subscription.declinedHeading')}>
          <p>
            {t('subscription.declinedBody', [
              planName(t, declined()?.requestedPlanCode ?? ''),
              format.date(declined()?.createdAt ?? ''),
            ])}
          </p>
          {declined()?.decisionNote ? <p className="mt-1">{declined()?.decisionNote}</p> : null}
        </Notice>
      ) : null}

      {can('CANCEL_SCHEDULED_CHANGE') && subscription.scheduledPlanCode ? (
        <Notice
          tone="warning"
          title={t('subscription.scheduledHeading')}
          actions={
            <Button
              variant="secondary"
              size="sm"
              loading={cancelling.loading}
              onClick={() => {
                void run(() =>
                  cancelChange().then(
                    (result) => result.data?.cancelScheduledPlanChange.userErrors ?? null,
                  ),
                )
              }}
            >
              {t('subscription.scheduledCancel')}
            </Button>
          }
        >
          {t('subscription.scheduledBody', [
            planName(t, subscription.scheduledPlanCode),
            format.date(subscription.scheduledEffectiveAt ?? ''),
          ])}
        </Notice>
      ) : null}

      {can('SCHEDULE_DOWNGRADE') && smaller.length > 0 && isPaidPlan(subscription.planCode) ? (
        <Section id="smaller-plan" title={t('subscription.downgradeHeading')}>
          <p className="text-body text-text-secondary">
            {t('subscription.downgradeBody', [format.date(subscription.periodEndsAt ?? '')])}
          </p>
          <div className="flex flex-wrap gap-2">
            {smaller.map((code) => (
              <Button
                key={code}
                variant="secondary"
                loading={scheduling.loading && target === code}
                onClick={() => {
                  setTarget(code)
                  void run(() =>
                    scheduleChange({ variables: { planCode: code, reason: null } }).then(
                      (result) => result.data?.schedulePlanChange.userErrors ?? null,
                    ),
                  )
                }}
              >
                {t('subscription.downgradeSubmit', [planName(t, code)])}
              </Button>
            ))}
          </div>
        </Section>
      ) : null}
      {sent ? (
        <p role="status" className="text-body-sm text-accent-text">
          {sent}
        </p>
      ) : null}
      {/* The other plans, last and in the product's framing: a section
          heading rather than the public site's headline, because here the
          tutor's own plan is the subject and this is what they could move to. */}
      <PricingSection
        context="product"
        plans={plans.map((plan) => ({
          ...plan,
          ...plan.capacities,
          prices: PLANS[plan.code].prices,
          features: plan.features,
        }))}
        currentPlanCode={subscription.planCode}
        renderAction={(plan) => {
          if (plan.code === subscription.planCode)
            return (
              <Button variant="secondary" disabled>
                {t('subscription.currentBadge')}
              </Button>
            )
          if (subscription.pendingRequest?.requestedPlanCode === plan.code)
            return (
              <Button variant="secondary" disabled>
                {t('subscription.requestPending')}
              </Button>
            )
          if (!can('REQUEST_UPGRADE') || subscription.pendingRequest || !larger.includes(plan.code))
            return null
          return (
            <Button
              variant="secondary"
              disabled={requesting.loading || sent !== null}
              loading={requesting.loading && target === plan.code}
              loadingText={t('subscription.upgradeSubmitting')}
              onClick={() => {
                setError(null)
                setSelectedPlan(plan.code)
              }}
            >
              {t('subscription.upgradeInterest', [planName(t, plan.code)])}
            </Button>
          )
        }}
      />
      <ConfirmationDialog
        open={selectedPlan !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedPlan(null)
        }}
        title={t('subscription.upgradeConfirmTitle', [
          selectedPlan ? planName(t, selectedPlan) : '',
        ])}
        // Asking loses nothing and is what the tutor came to do, so the send is
        // the primary action; "Not now" still comes first.
        intent="default"
        description={t('subscription.upgradeConfirmBody', [
          selectedPlan ? planName(t, selectedPlan) : '',
        ])}
        confirmLabel={t('subscription.upgradeSubmit')}
        cancelLabel={t('subscription.upgradeCancel')}
        onConfirm={() => {
          const code = selectedPlan
          if (!code || requesting.loading || !can('REQUEST_UPGRADE') || subscription.pendingRequest)
            return
          setTarget(code)
          void run(() =>
            requestUpgrade({ variables: { planCode: code, message: message.trim() || null } }).then(
              (result) => {
                const payload = result.data?.requestPlanUpgrade
                if (!payload) throw new Error('Missing upgrade request response')
                if (payload.userErrors.length === 0) {
                  setSent(t('subscription.upgradeSent', [planName(t, code)]))
                  setMessage('')
                }
                return payload.userErrors
              },
            ),
          )
        }}
      >
        <Textarea
          label={t('subscription.upgradeMessageLabel')}
          optional
          rows={2}
          value={message}
          placeholder={t('subscription.upgradeMessagePlaceholder')}
          onChange={(event) => setMessage(event.target.value)}
        />
      </ConfirmationDialog>
    </div>
  )

  /** The most recent declined request, for the one notice that reports it. */
  function declined() {
    return subscription.recentRequests.find((request) => request.status === 'DECLINED')
  }

  /**
   * Run a mutation and surface whatever the server refused with.
   *
   * The API names why it refused and the dictionary words it, so the fallback
   * here is only for a refusal with no reason attached — which should not
   * happen, and is better than a blank screen when it does.
   */
  async function run(call: PlanCall) {
    setError(null)
    setSent(null)
    lastCall.current = call
    try {
      const errors = await call()
      setFailure(undefined)
      // No payload at all is not a success: nothing says the change was made.
      if (errors === null) setFailure(new Error('The change came back without a result.'))
      else if (errors.length > 0) setError(userErrorText(errors, t('common.tryAgain')))
    } catch (caught) {
      // A rejected request — the network, or a thrown GraphQL error. Said
      // beside the plan, with a retry of the same change: every one of these
      // is safe to send twice, because the second finds the change made (or
      // the request already waiting) and refuses in words.
      setFailure(caught)
    }
  }
}
